// src/utils/parseRooms.js
// ---------------------------------------------------------------------------
// The regions of a parsed plan, as the room schedule and the 3D model use them.
//
// Moved out of PlanParseView so the 3D view can build a drawing project's model
// by itself, from the stored parse, with exactly the rooms the Design tab shows.
// Two copies of this list would be two chances for the schedule and the model to
// disagree about what a room is.
// ---------------------------------------------------------------------------
import { spaceOf } from './spaceTypes.js';
import { readingOrder } from './planGeometry.js';

/**
 * Detected regions in reading order, shaped for RoomTable and RoomBadge.
 *
 * The CV emits polygons; the table reports each one's extent and true area, so
 * the numbers here describe what was actually detected rather than a tidied
 * rectangle. Rectangularising happens in the editor, where the user can see it.
 */
export function numberedRooms(parse, merge) {
  // A matched AI reading names regions the CV pass could only number. The label
  // is the printed one, so "Unclassified" becomes "KITCHEN" and the schedule
  // stops being a list of anonymous rectangles — without any coordinate moving,
  // because the geometry is still entirely CV's.
  const named = new Map(
    (merge?.ok ? merge.matches : []).map((m) => [m.regionId, m]),
  );
  const items = (parse.rooms || [])
    .filter((r) => (r.polygon || []).length >= 3)
    .map((r) => {
      const hit = named.get(r.id);
      const label = hit ? hit.label : r.label;
      const sp = spaceOf(label);
      return { id: r.id, pts: r.polygon, type: sp ? sp.key : null,
               label, panel: r.panel ?? null,
               // carried through so the schedule can show what was PRINTED
               // beside what was MEASURED, and a mismatch is visible
               printedSize: hit ? hit.sizeText : null,
               printedAreaM2: hit ? hit.areaM2 : null };
    });
  return readingOrder(items, parse.imageSize?.h || 1000);
}

/**
 * The rooms that become volumes: numbered, with off-plan regions (sheet border,
 * title block, gaps between plans) dropped — the filter the Model button applies.
 */
export function roomsForMassing(parse, merge) {
  const hasPanels = (parse?.panels || []).length > 0;
  return numberedRooms(parse, merge).filter((r) => !hasPanels || r.panel != null);
}
